import { FilePlus2 } from 'lucide-react';
import { useMemo } from 'react';
import type { TemplateVersion } from 'used-vehicle-contracts';
import { Button } from '../../../components/ui/Button';
import { DataTable, type DataTableColumns } from '../../../components/ui/DataTable';
import { StatusText } from '../../../components/ui/StatusText';
import { formatDay, formatText } from '../../../lib/format';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { versionStatusTone } from '../templates-display';

export interface TemplateVersionsTableProps {
  versions: readonly TemplateVersion[];
  hasDraft: boolean;
  isOpening: boolean;
  onOpenAsDraft: (versionId: string) => void;
}

/** Every version this template has ever had, newest first, with the reason it was published for. */
export const TemplateVersionsTable = ({ versions, hasDraft, isOpening, onOpenAsDraft }: TemplateVersionsTableProps) => {
  const { t } = useI18nTranslation();

  const columns = useMemo<DataTableColumns<TemplateVersion>>(
    () => [
      {
        id: 'version',
        header: t('templates.columnVersion'),
        accessorFn: (row) => row.version,
        cell: ({ row }) => (
          <span
            dir="ltr"
            className="inline-block font-mono text-sm tabular-nums text-foreground"
          >
            v{row.original.version}
          </span>
        ),
      },
      {
        id: 'status',
        header: t('templates.columnStatus'),
        accessorFn: (row) => row.status,
        cell: ({ row }) => (
          <StatusText
            label={t(`templates.versionStatuses.${row.original.status}`)}
            tone={versionStatusTone(row.original.status)}
          />
        ),
      },
      {
        id: 'publishedAt',
        header: t('templates.columnPublishedAt'),
        accessorFn: (row) => row.publishedAt ?? '',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.publishedAt === null ? t('common.notRecorded') : formatDay(row.original.publishedAt)}
            {row.original.publishedByName ? ` · ${row.original.publishedByName}` : ''}
          </span>
        ),
      },
      {
        id: 'reason',
        header: t('templates.columnReason'),
        enableSorting: false,
        cell: ({ row }) => <span className="text-sm text-muted-foreground">{formatText(row.original.reason)}</span>,
      },
      {
        id: 'revision',
        header: t('templates.columnRevision'),
        accessorFn: (row) => row.revision,
        cell: ({ row }) => (
          <span
            dir="ltr"
            className="inline-block font-mono text-xs tabular-nums text-muted-foreground"
          >
            {row.original.revision}
          </span>
        ),
      },
      {
        id: 'actions',
        header: t('templates.columnActions'),
        enableSorting: false,
        cell: ({ row }) =>
          row.original.status === 'DRAFT' ? null : (
            <Button
              variant="ghost"
              size="sm"
              disabled={hasDraft || isOpening}
              title={hasDraft ? t('templates.draftAlreadyOpen') : undefined}
              onClick={() => onOpenAsDraft(row.original.id)}
            >
              <FilePlus2 className="size-3.5" />
              {t('templates.openAsDraft')}
            </Button>
          ),
      },
    ],
    [hasDraft, isOpening, onOpenAsDraft, t],
  );

  return (
    <DataTable
      columns={columns}
      data={[...versions]}
      emptyMessage={t('templates.noVersions')}
    />
  );
};
